"use client";

import React, { useEffect, useCallback, useMemo } from "react";
import { useCheckoutTerm } from "../hooks/use-checkout-term";
import { poolCapsForKeys } from "../../../lib/checkout/pool-availability";
import type { CustomerBookingHistory } from "../../../lib/checkout/types";
import { CheckoutPanel } from "../checkout-panel";
import { CheckoutFooter } from "../checkout-footer";
import { CheckoutSummaryCard } from "../checkout-summary-card";
import { DateSelectionStep } from "../date-selection-step";
import { TicketSelectionStep } from "../ticket-selection-step";
import { TicketSelectionStepV2 } from "../ticket-selection-step-v2";
import { AddOnsStep } from "../add-ons-step";
import { UserFormStep } from "../user-form-step";
import { ParticipantsStep, participantsToPayload } from "../participants-step";
import type {
  CheckoutParticipantField,
  ExistingParticipant,
} from "../participants-step";
import { BasketChoiceStep, type BasketChoiceNotice } from "../basket-choice-step";
import { ProgressBar } from "../progress-bar";
import { ToastProvider } from "../checkout-toast";
import { formatDate, formatTimeRange, scrollCheckoutContentToTop } from "../utils";
import { formatPolicyHint } from "../utils/format-policy-hint";
import { buildFallbackBookingPolicy } from "../utils/build-fallback-policy";
import { resolveSummaryLocationName } from "../utils/session-location";
import { TriangleAlert, Loader2 } from "lucide-react";
import type { DiscountResult } from "../utils/types";

export type CheckoutTermProps = {
  // Data
  course: any;
  sessions?: any[];
  ticketTypes?: any[];
  ticketTypesV2?: any[];
  /** Equipment caps from the orchestrator's pool-availability read. */
  poolAvailability?: import("../../../lib/checkout/pool-availability").CheckoutPoolAvailability | null;
  priorPurchasedTicketIds?: string[];
  customerBookingHistory?: CustomerBookingHistory;
  cartPricingRules?: any[];
  addOns?: any[];
  addOnGroups?: any[];
  user?: any;
  bundleID?: string; // If using a purchased bundle, payment is skipped
  bundle?: any; // Bundle info for pre-filling user details
  locations?: any[]; // For location display
  tutors?: any[]; // For tutor info display
  /** Pre-select a session by ID (skips date selection step) */
  initialSessionId?: string;

  // Callbacks
  onLoadMoreDates: (args: { page: number; limit: number }) => Promise<any>;
  onConfirmFreeBooking?: (data: any) => Promise<void>;
  onValidateDiscount?: (code: string, email: string) => Promise<DiscountResult>;
  onSuccess?: (purchase: any) => void;
  onError?: (error: Error | string) => void;
  onClose?: () => void;
  onStepChange?: (step: string) => void; // Callback when step changes
  onJoinWaitlist?: (session?: any) => void; // Waiting list callback
  // Config
  currency?: string;
  timezone?: string;
  cancellationPolicyUrl?: string;
  /** Terms settings for dialog display */
  termsSettings?: import("../utils/types").TermsSettings;
  /** Custom terms for dialog display */
  customTerms?: import("../utils/types").CustomTerms;
  /** Callback for webcomponents to open terms dialog in external portal */
  onOpenTermsDialog?: (data: {
    termsSettings?: import("../utils/types").TermsSettings;
    customTerms?: import("../utils/types").CustomTerms;
    timezone?: string;
  }) => void;
  isSendingMaterial?: boolean;

  // School/Business settings
  school?: {
    name?: string;
    _id?: string;
    isNewsletterSignupEnabled?: boolean;
    customNewsletterSignupText?: string | null;
    businessCountry?: string;
  };
  allowPrefillOthersGuestsOnCheckout?: boolean;
  // Participants (attendees) — threaded from the orchestrator/widget.
  participantsEnabled?: boolean;
  participantLabel?: string;
  participantFields?: CheckoutParticipantField[];
  existingParticipants?: ExistingParticipant[];
  buyerIsAttendee?: boolean;
  /** Name-later listing: the attendee step accepts empty places. */
  allowUnnamedAttendees?: boolean;
  /** The signed-in customer's identity, for naming the buyer on their own card. */
  customer?: { firstName?: string; lastName?: string; email?: string; phone?: string };
  /** Answers the buyer has already given about themselves elsewhere in this basket. */
  selfSeed?: Record<string, unknown>;
  onSelfAnswersChange?: (answers: Record<string, unknown>) => void;
  /** When true, footer is not sticky - flows after content (for iframe embeds) */
  isEmbedMode?: boolean;
  /** When true, hides back navigation to options/dates (timetable Book Now) */
  isDirectBooking?: boolean;

  // Basket feature
  /** When true, shows the bundle/membership helper (widget-only, basket-enabled). */
  showBundleMembershipHelp?: boolean;
  /** Current number of items in the basket */
  basketItemCount?: number;
  /**
   * Per-session quantity this customer has already added to their basket
   * for this course. Subtracted from displayed "spots left".
   */
  reservedSessionQuantities?: Record<string, number>;
  /** Callback when user clicks "Add to Basket" */
  onAddToBasket?: (itemData: {
    type: "booking";
    subType: "term";
    courseId: string;
    courseName: string;
    sessionId: string;
    sessionDate: string;
    /** The LISTING's zone, so the basket dates this row on the same
     *  clock as an appointment row beside it rather than the browser's. */
    timezone?: string;
    termName?: string;
    ticketSelections: Array<{
      ticketTypeId: string;
      ticketTypeName: string;
      quantity: number;
      price: number;
    }>;
    addOns?: Array<{
      addOnId: string;
      addOnName: string;
      quantity: number;
      price: number;
    }>;
    userInputs?: Array<{
      index: number;
      guestIndex: number | null;
      label: string;
      value: string | boolean | Date | null;
    }>;
    guestDetails?: {
      firstName: string;
      lastName: string;
      email: string;
      phone: string;
    };
    checkoutGuestsInformation?: Array<{
      firstName: string;
      lastName: string;
      email: string;
      phoneNumber: string;
      marketingOptIn?: boolean;
      mandatoryTickBox?: boolean;
    }>;
    participants?: Array<{
      id?: string;
      answers: Array<{ slug: string; value: unknown }>;
    }>;
    price: number;
    currency: string;
    imageUrl?: string;
    isOnline?: boolean;
  }) => void;
  /** Callback when user clicks "Proceed to Checkout" in BasketChoiceStep — adds item to basket and navigates to basket checkout page */
  onProceedToBasketCheckout: (itemData: {
    type: "booking";
    courseId: string;
    courseName: string;
    sessionId?: string;
    sessionDate: string;
    /** The LISTING's zone, so the basket dates this row on the same
     *  clock as an appointment row beside it rather than the browser's. */
    timezone?: string;
    ticketSelections: Array<{
      ticketTypeId: string;
      ticketTypeName: string;
      quantity: number;
      price: number;
    }>;
    addOns?: Array<{
      addOnId: string;
      addOnName: string;
      quantity: number;
      price: number;
    }>;
    userInputs?: Array<{
      index: number;
      guestIndex: number | null;
      label: string;
      value: string | boolean | Date | null;
    }>;
    guestDetails?: {
      firstName: string;
      lastName: string;
      email: string;
      phone: string;
    };
    checkoutGuestsInformation?: Array<{
      firstName: string;
      lastName: string;
      email: string;
      phoneNumber: string;
      marketingOptIn?: boolean;
      mandatoryTickBox?: boolean;
    }>;
    participants?: Array<{
      id?: string;
      answers: Array<{ slug: string; value: unknown }>;
    }>;
    price: number;
    currency: string;
    imageUrl?: string;
    isOnline?: boolean;
  }) => void;
  /** Callback to trigger login flow. When provided and user is not logged in, shows "Log in" prompt in payment methods. */
  onLogin?: () => void;
  /**
   * When true, skip the basket choice step and auto-fire onProceedToBasketCheckout.
   */
  directCheckout?: boolean;
  /** Why the host refused the last basket choice; shown on the basket choice step. */
  basketConflict?: BasketChoiceNotice | null;
};

export function CheckoutTerm({
  course,
  sessions,
  ticketTypes,
  ticketTypesV2,
  priorPurchasedTicketIds,
  customerBookingHistory,
  cartPricingRules = [],
  addOns = [],
  addOnGroups = [],
  user,
  bundleID,
  bundle,
  locations = [],
  tutors = [],
  initialSessionId,
  onLoadMoreDates,
  onValidateDiscount,
  onClose,
  onStepChange,
  onJoinWaitlist,
  currency = "GBP",
  timezone,
  cancellationPolicyUrl = "/terms",
  termsSettings,
  customTerms,
  onOpenTermsDialog,
  isSendingMaterial = false,
  school,
  allowPrefillOthersGuestsOnCheckout = false,
  participantsEnabled = false,
  participantLabel = "Attendees",
  participantFields = [],
  existingParticipants = [],
  buyerIsAttendee = false,
  allowUnnamedAttendees = false,
  customer,
  selfSeed,
  onSelfAnswersChange,
  isEmbedMode = false,
  isDirectBooking = false,
  basketItemCount = 0,
  reservedSessionQuantities,
  onAddToBasket,
  onProceedToBasketCheckout,
  directCheckout = false,
  basketConflict = null,
  showBundleMembershipHelp = false,
  poolAvailability = null,
}: CheckoutTermProps) {
  // Effective bundleID: pre-selected via prop (bundle redemption)
  const effectiveBundleID = bundleID || undefined;

  const checkout = useCheckoutTerm({
    course,
    sessions,
    ticketTypes,
    ticketTypesV2,
    priorPurchasedTicketIds,
    customerBookingHistory,
    enabledAddOns: addOns,
    addOnGroups,
    locations,
    user,
    bundleID,
    bundle,
    isSendingMaterial,
    initialSessionId,
    reservedSessionQuantities,
    allowPrefillOthersGuestsOnCheckout,
    participantsEnabled,
    hasParticipantFields: participantFields.length > 0,
    onLoadMoreDates,
    onValidateDiscount,
  });

  // Handle add to basket
  const handleAddToBasket = useCallback(() => {
    if (!onAddToBasket || !checkout.selectedSession) return;

    // Build ticket selections with full ticket type data
    const ticketSelections = checkout.ticketSelections
      .filter((sel: any) => sel.quantity > 0)
      .map((sel: any) => {
        const ticketType = checkout.ticketTypes.find(
          (tt: any) => tt.id === sel.ticketTypeId
        );
        const v2Result = checkout.v2PricingResults?.find(
          (r: any) => r.ticketTypeId === sel.ticketTypeId,
        );
        const lineTotal = v2Result?.lineTotal;
        return {
          ticketTypeId: sel.ticketTypeId,
          ticketTypeName: ticketType?.name || "Ticket",
          quantity: sel.quantity,
          price: ticketType?.effectivePrice ?? ticketType?.price ?? 0,
          lineTotal,
          numberOfGuestsPerTicket: ticketType?.numberOfGuestsPerTicket,
          typeCharge: ticketType?.typeCharge,
          bookingPolicy: ticketType?.bookingPolicy ?? undefined,
        };
      });

    // Build add-ons from checkout state
    const addOnSelections = checkout.addOnSelections
      .filter((sel: any) => sel.quantity > 0)
      .map((sel: any) => {
        const allAddOns = [...checkout.enabledAddOns, ...(checkout.addOnGroups || []).flatMap((g: any) => g.addOns)];
        const addOn = allAddOns.find(
          (a: any) => a.id === sel.addOnId
        );
        return {
          addOnId: sel.addOnId,
          addOnName: addOn?.name || "Add-on",
          quantity: sel.quantity,
          price: addOn?.price || 0,
          groupId: sel.groupId || null,
          groupName: sel.groupName || null,
        };
      });

    onAddToBasket({
      type: "booking",
      bookingType: "term",
      courseId: course._id || course.id,
      courseName: course.title,
      sessionId: checkout.selectedSession.id,
      sessionDate: checkout.selectedSession.startDateTime,
      timezone: timezone || course.timezone,
      termName: checkout.selectedSession.termName,
      ticketSelections,
      addOns: addOnSelections.length > 0 ? addOnSelections : undefined,
      userInputs: checkout.userFormValues?.length ? checkout.userFormValues : undefined,
      participants: checkout.showParticipantsStep
        ? participantsToPayload(checkout.participantsValue)
        : undefined,
      price: checkout.total,
      currency: currency,
      imageUrl: course.images?.[0]?.url,
      isOnline: checkout.selectedSession.isOnline ?? course.isOnline,
      guestDetails: {
        firstName: checkout.personalDetails.guests[0]?.firstName || "",
        lastName: checkout.personalDetails.guests[0]?.lastName || "",
        email: checkout.personalDetails.guests[0]?.email || "",
        phone: checkout.personalDetails.guests[0]?.phoneNumber || "",
      },
      checkoutGuestsInformation: checkout.personalDetails.guests.map((g: any) => ({
        firstName: g.firstName || "",
        lastName: g.lastName || "",
        email: g.email || "",
        phoneNumber: g.phoneNumber || "",
        marketingOptIn: g.marketingOptIn || false,
        mandatoryTickBox: g.mandatoryTickBox,
      })),
    });
  }, [
    timezone,
    onAddToBasket,
    checkout.selectedSession,
    checkout.ticketSelections,
    checkout.addOnSelections,
    checkout.ticketTypes,
    checkout.enabledAddOns,
    checkout.total,
    checkout.personalDetails,
    checkout.userFormValues,
    checkout.v2PricingResults,
    checkout.addOnGroups,
    // A stale attendee list here sent `participants: []` from every
    // direct-checkout host (Oct 2026) — see checkout-regular.
    checkout.showParticipantsStep,
    checkout.participantsValue,
    course,
    currency,
  ]);

  const handleProceedToBasketCheckout = useCallback(() => {
    if (!checkout.selectedSession) return;

    const ticketSelections = checkout.ticketSelections
      .filter((sel: any) => sel.quantity > 0)
      .map((sel: any) => {
        const ticketType = checkout.ticketTypes.find(
          (tt: any) => tt.id === sel.ticketTypeId
        );
        const v2Result = checkout.v2PricingResults?.find(
          (r: any) => r.ticketTypeId === sel.ticketTypeId,
        );
        const lineTotal = v2Result?.lineTotal;
        return {
          ticketTypeId: sel.ticketTypeId,
          ticketTypeName: ticketType?.name || "Ticket",
          quantity: sel.quantity,
          price: ticketType?.effectivePrice ?? ticketType?.price ?? 0,
          lineTotal,
          numberOfGuestsPerTicket: ticketType?.numberOfGuestsPerTicket,
          typeCharge: ticketType?.typeCharge,
        };
      });

    const addOnSelections = checkout.addOnSelections
      .filter((sel: any) => sel.quantity > 0)
      .map((sel: any) => {
        const allAddOns = [...checkout.enabledAddOns, ...(checkout.addOnGroups || []).flatMap((g: any) => g.addOns)];
        const addOn = allAddOns.find(
          (a: any) => a.id === sel.addOnId
        );
        return {
          addOnId: sel.addOnId,
          addOnName: addOn?.name || "Add-on",
          quantity: sel.quantity,
          price: addOn?.price || 0,
          groupId: sel.groupId || null,
          groupName: sel.groupName || null,
        };
      });

    onProceedToBasketCheckout({
      type: "booking",
      bookingType: "term",
      courseId: course._id || course.id,
      courseName: course.title,
      sessionId: checkout.selectedSession.id,
      sessionDate: checkout.selectedSession.startDateTime,
      timezone: timezone || course.timezone,
      ticketSelections,
      addOns: addOnSelections.length > 0 ? addOnSelections : undefined,
      userInputs: checkout.userFormValues?.length ? checkout.userFormValues : undefined,
      participants: checkout.showParticipantsStep
        ? participantsToPayload(checkout.participantsValue)
        : undefined,
      price: checkout.total,
      currency: currency,
      imageUrl: course.images?.[0]?.url,
      isOnline: checkout.selectedSession.isOnline ?? course.isOnline,
      guestDetails: {
        firstName: checkout.personalDetails.guests[0]?.firstName || "",
        lastName: checkout.personalDetails.guests[0]?.lastName || "",
        email: checkout.personalDetails.guests[0]?.email || "",
        phone: checkout.personalDetails.guests[0]?.phoneNumber || "",
      },
      checkoutGuestsInformation: checkout.personalDetails.guests.map((g: any) => ({
        firstName: g.firstName || "",
        lastName: g.lastName || "",
        email: g.email || "",
        phoneNumber: g.phoneNumber || "",
        marketingOptIn: g.marketingOptIn || false,
        mandatoryTickBox: g.mandatoryTickBox,
      })),
    });
  }, [
    timezone,
    onProceedToBasketCheckout,
    checkout.selectedSession,
    checkout.ticketSelections,
    checkout.addOnSelections,
    checkout.ticketTypes,
    checkout.enabledAddOns,
    checkout.total,
    checkout.personalDetails,
    checkout.userFormValues,
    checkout.v2PricingResults,
    checkout.addOnGroups,
    // A stale attendee list here sent `participants: []` from every
    // direct-checkout host (Oct 2026) — see checkout-regular.
    checkout.showParticipantsStep,
    checkout.participantsValue,
    course,
    currency,
  ]);

  const fallbackBookingPolicy = useMemo(
    () => buildFallbackBookingPolicy(termsSettings),
    [termsSettings],
  );

  const handlePolicyClick = useMemo(() => {
    if (!onOpenTermsDialog) return undefined;
    return (ticketName: string, policy: any) => {
      onOpenTermsDialog({
        ticketPolicies: [{ ticketName, policy }],
      });
    };
  }, [onOpenTermsDialog]);

  // Step labels for progress bar
  const stepLabels: Record<string, string> = {
    dates: "Date selection",
    tickets: "Tickets",
    addons: "Add-ons",
    participants: participantLabel,
    userform: "Customer Information",
    personal: "Personal details",
    basketChoice: "Basket",
    payment: "Complete Booking",
  };

  // Progress bar steps
  const progressSteps = checkout.visibleSteps.map((step: string) => ({
    id: step,
    label: stepLabels[step] || step,
    shortLabel:
      {
        dates: "Date",
        tickets: "Tickets",
        addons: "Add-ons",
        participants: participantLabel,
        userform: "Info",
        personal: "Details",
        basketChoice: "Basket",
        payment: "Payment",
      }[step] || step,
  }));

  // Call onStepChange when step changes and scroll to top
  useEffect(() => {
    onStepChange?.(checkout.step);
    // Scroll the checkout's own scroll container to top on step change. Shadow-DOM
    // aware and never scrolls the host window (would yank an embedded page to top).
    setTimeout(() => scrollCheckoutContentToTop(), 50);
  }, [checkout.step, onStepChange]);

  // Format selected date for display
  const formattedDate = (() => {
    if (!checkout.selectedSession) return undefined;

    const session = checkout.selectedSession;
    const tz = timezone || course.timezone;
    // Use session's isOnline if available, fall back to course's isOnline
    const isOnline = session.isOnline ?? course.isOnline ?? false;

    // For online sessions, use user's local timezone; for offline, use course timezone
    const displayTimezone = isOnline && typeof window !== "undefined"
      ? Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone || tz
      : tz;

    // For terms, use the term name as the primary label
    if (session.termName) {
      // Try to get the first class date for additional context
      if (
        session.startDateTime &&
        !isNaN(new Date(session.startDateTime).getTime())
      ) {
        return `${session.termName}, ${formatDate(session.startDateTime, displayTimezone)} at ${formatTimeRange(session.startDateTime, session.duration, tz, isOnline)}`;
      }
      // Fallback to just the term name
      return session.termName;
    }

    // For regular sessions, format the date/time
    if (
      session.startDateTime &&
      !isNaN(new Date(session.startDateTime).getTime())
    ) {
      return `${formatDate(session.startDateTime, displayTimezone)} at ${formatTimeRange(session.startDateTime, session.duration, tz, isOnline)}`;
    }

    return undefined;
  })();

  // Venue for the sidebar summary and the review step (rationale lives on
  // resolveSummaryLocationName). Plain call — it's a cheap array lookup.
  const summaryLocationLabel = resolveSummaryLocationName(
    course,
    locations,
    checkout.selectedSession,
  );

  // Build ticket items for summary
  const ticketItems = checkout.ticketSelections
    .map((sel) => {
      const ticketType = checkout.ticketTypes.find(
        (tt: any) => tt.id === sel.ticketTypeId,
      );
      if (!ticketType || sel.quantity === 0) return null;

      // Use effectivePrice (includes perPersonPerLesson multiplier) if available
      const displayPrice = ticketType.effectivePrice ?? ticketType.price;
      const displayDepositAmount =
        ticketType.effectiveDepositAmount ?? ticketType.depositAmount;

      const rawTicket = ticketTypesV2?.find((tt: any) => tt.id === sel.ticketTypeId);
      const v2Result = checkout.v2PricingResults?.find(
        (r: any) => r.ticketTypeId === sel.ticketTypeId,
      );

      // For deposit tickets: price is the deposit, fullPrice the regular price.
      // A V2 `chargeType:"deposit"` ticket carries its split on the pricing
      // result (per-unit figures pre-scaled by the hook), not the legacy
      // `ticketTypeGroup:"deposit"` marker.
      const isV2Deposit =
        v2Result?.chargeType === "deposit" && v2Result?.depositAmount != null;
      const isDeposit = ticketType.ticketTypeGroup === "deposit" || isV2Deposit;
      const depositPrice = isV2Deposit
        ? (v2Result.depositUnit ?? displayPrice)
        : isDeposit
          ? (displayDepositAmount ?? displayPrice)
          : displayPrice;
      const fullPrice = isV2Deposit ? (v2Result.fullUnit ?? displayPrice) : displayPrice;
      const lineTotal = !isDeposit ? v2Result?.lineTotal : undefined;
      return {
        name: ticketType.name,
        price: depositPrice,
        fullPrice: isDeposit ? fullPrice : undefined,
        lineTotal,
        count: sel.quantity,
        ticketTypeGroup: ticketType.ticketTypeGroup,
        isDeposit,
        depositNowTotal: isV2Deposit ? (v2Result?.depositLinePence ?? undefined) : undefined,
        depositContractTotal: isV2Deposit
          ? (v2Result?.depositContractPence ?? undefined)
          : undefined,
        remainderCollection: isV2Deposit ? v2Result?.remainderCollection : undefined,
        balanceDueDate: isV2Deposit ? v2Result?.balanceDueDate : undefined,
        instalmentPreview: isV2Deposit ? v2Result?.instalmentPreview : undefined,
        instalmentDiscountShares: isV2Deposit
          ? (checkout.v2InstalmentDiscountShares?.get(sel.ticketTypeId) ?? null)
          : undefined,
        numberOfGuestsPerTicket: ticketType.numberOfGuestsPerTicket ?? 1,
        policyHint: formatPolicyHint(rawTicket?.bookingPolicy),
        bookingPolicy: rawTicket?.bookingPolicy ?? ticketType.bookingPolicy ?? null,
      };
    })
    .filter(Boolean) as {
    name: string;
    price: number;
    fullPrice?: number;
    lineTotal?: number;
    count: number;
    ticketTypeGroup?: string;
    isDeposit?: boolean;
    depositNowTotal?: number;
    depositContractTotal?: number;
    remainderCollection?: "payment_link" | "in_person" | null;
    balanceDueDate?: string | null;
    instalmentPreview?: Array<{
      label: string | null;
      amountLinePence?: number;
      amountUnitPence: number;
      dueType: string;
      dueDate: string | null;
      collection: "payment_link" | "in_person";
      offlineNote: string | null;
    }> | null;
    instalmentDiscountShares?: number[] | null;
    numberOfGuestsPerTicket: number;
    policyHint?: string;
    bookingPolicy?: any;
  }[];

  // Build ticket policies for terms dialog
  const ticketPolicies = checkout.ticketSelections
    .map((sel) => {
      const rawTicket = ticketTypesV2?.find((tt: any) => tt.id === sel.ticketTypeId);
      if (!rawTicket?.bookingPolicy || sel.quantity === 0) return null;
      return {
        ticketName: rawTicket.name,
        policy: rawTicket.bookingPolicy,
      };
    })
    .filter(Boolean) as Array<{ ticketName: string; policy: any }>;

  // Build add-on items for summary
  const addOnItems = checkout.addOnSelections
    .map((sel) => {
      const allAddOns = [...checkout.enabledAddOns, ...(checkout.addOnGroups || []).flatMap((g: any) => g.addOns)];
      const addOn = allAddOns.find(
        (a: any) => a.id === sel.addOnId,
      );
      if (!addOn || sel.quantity === 0) return null;
      return {
        name: addOn.name,
        price: addOn.price,
        count: sel.quantity,
        groupId: sel.groupId || null,
        groupName: sel.groupName || null,
      };
    })
    .filter(Boolean) as { name: string; price: number; count: number; groupId: string | null; groupName: string | null }[];

  return (
    <ToastProvider>
      <CheckoutPanel isEmbedMode={isEmbedMode}>
        <CheckoutPanel.Header
          title={course.title}
          subtitle="Term Booking"
          onClose={isDirectBooking ? undefined : onClose}
        />

        <CheckoutPanel.Content isEmbedMode={isEmbedMode}>
          <CheckoutPanel.Main>
            {/* Progress Bar */}
            <ProgressBar
              steps={progressSteps}
              currentStep={checkout.currentStepIndex}
              onStepClick={(index) => {
                // In direct booking mode, prevent going back to dates step
                if (isDirectBooking && checkout.visibleSteps[index] === "dates") {
                  return;
                }
                checkout.goToStep(checkout.visibleSteps[index]!);
              }}
              className="mb-6"
            />

            {/* Date Selection Step */}
            {checkout.step === "dates" && (
              <DateSelectionStep
                sessions={checkout.sessions}
                selectedSessionId={checkout.selectedSession?.id}
                onSelectSession={(session) => checkout.selectSession(session)}
                hasMoreDates={!checkout.isFullyLoaded}
                onLoadMore={checkout.loadMoreDates}
                isLoading={checkout.isLoading}
                currency={currency}
                timezone={timezone || course.timezone}
                tutors={tutors.map((t: any) => ({
                  id: t._id || t.id,
                  name: t.name,
                  fullName: t.fullName,
                  imageUrl: t.profile?.imageUrl || t.imageUrl,
                }))}
                onJoinWaitlist={onJoinWaitlist}
                schoolId={school?._id}
                hidePrice={!!effectiveBundleID}
                isFromPrice={course.priceDisplayMode === "manual" && course.isPriceFrom === true}
                hideDisplayAvailableTickets={course?.displaySettings?.hideDisplaysAvailableTickets ?? false}
                headerTitle="Choose a Term"
                headerDescription="Pick a term schedule that fits your availability."
                reservedSessionQuantities={reservedSessionQuantities}
              />
            )}

            {/* Ticket Selection Step */}
            {checkout.step === "tickets" && !checkout.selectedSession && (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            )}
            {checkout.step === "tickets" && checkout.selectedSession && (
              <>
                {checkout.isV2 ? (
                  <TicketSelectionStepV2
                    ticketTypes={checkout.ticketTypes}
                    selections={checkout.ticketSelections}
                    onSelectionChange={checkout.updateTicketSelection}
                    currency={currency}
                    totalAvailability={checkout.selectedSession?.availability}
                    hidePrice={!!effectiveBundleID}
                    bundleMaxTickets={effectiveBundleID && bundle ? (bundle.totalSessions - (bundle.usedSessions || 0)) : undefined}
                    constraintErrors={checkout.constraintErrors}
                    cartPricingRules={cartPricingRules}
                    fallbackBookingPolicy={fallbackBookingPolicy}
                    onPolicyClick={handlePolicyClick}
                    showBundleMembershipHelp={showBundleMembershipHelp}
                    // Term sessions already carry the term-min in the caps
                    // (the endpoint folds the all-or-nothing aggregate into
                    // each occurrence's entry), so the plain session join is
                    // the correct number here too.
                    poolMaxTicketsByTicketTypeId={poolCapsForKeys(
                      poolAvailability,
                      checkout.selectedSession?.id
                        ? [checkout.selectedSession.id]
                        : [],
                    )}
                  />
                ) : (
                  <TicketSelectionStep
                    ticketTypes={checkout.ticketTypes}
                    selections={checkout.ticketSelections}
                    onSelectionChange={checkout.updateTicketSelection}
                    currency={currency}
                    totalAvailability={checkout.selectedSession?.availability}
                    hidePrice={!!effectiveBundleID}
                    bundleMaxTickets={effectiveBundleID && bundle ? (bundle.totalSessions - (bundle.usedSessions || 0)) : undefined}
                    fallbackBookingPolicy={fallbackBookingPolicy}
                    onPolicyClick={handlePolicyClick}
                    showBundleMembershipHelp={showBundleMembershipHelp}
                  />
                )}

                {checkout.hasMixedPolicies && (
                  <div className="flex items-start gap-2.5 p-3 mt-4 bg-amber-50 border border-amber-200 rounded-lg text-sm">
                    <TriangleAlert className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                    <p className="text-amber-800">
                      The selected tickets have different cancellation policies and cannot be combined in the same booking. Please select tickets that share the same policy.
                    </p>
                  </div>
                )}
              </>
            )}

            {/* Add-ons Step */}
            {checkout.step === "addons" && (
              <AddOnsStep
                addOns={checkout.enabledAddOns}
                groups={checkout.addOnGroups}
                selections={checkout.addOnSelections}
                onSelectionChange={checkout.updateAddOnSelection}
                currency={currency}
              />
            )}

            {/* User Form Step */}
            {checkout.step === "participants" && (
              <ParticipantsStep
                sessionDate={checkout.selectedSession?.startDateTime}
                participantLabel={participantLabel}
                fields={participantFields}
                existing={existingParticipants}
                maxParticipants={Math.max(1, checkout.totalGuestCount)}
                ticketGroups={checkout.ticketSeatGroups}
                value={checkout.participantsValue}
                onChange={checkout.setParticipantsValue}
                onValidationChange={checkout.setIsParticipantsValid}
                buyerIsAttendee={buyerIsAttendee}
                allowUnnamed={allowUnnamedAttendees}
                buyerSeededRef={checkout.buyerAttendeeSeededRef}
                selfSeed={selfSeed}
                onSelfAnswersChange={onSelfAnswersChange}
                buyer={{
                  // The typed personal-details step wins; the signed-in
                  // customer's own record fills in when this flow skips it
                  // (basket-enabled listings collect the buyer downstream).
                  firstName: checkout.personalDetails.guests[0]?.firstName || customer?.firstName,
                  lastName: checkout.personalDetails.guests[0]?.lastName || customer?.lastName,
                  email: checkout.personalDetails.guests[0]?.email || customer?.email,
                  phone: checkout.personalDetails.guests[0]?.phoneNumber || customer?.phone,
                }}
              />
            )}

            {checkout.step === "userform" && (
              <UserFormStep
                questions={course.userQuestionForm?.questions || []}
                totalGuests={checkout.personalDetails.guests.length}
                collectSystemGuests={checkout.collectSystemGuests}
                participantsEnabled={checkout.showParticipantsStep}
                values={checkout.userFormValues}
                onChange={checkout.setUserFormValues}
                onValidationChange={checkout.setIsUserFormValid}
                mode={course.userQuestionForm?.mode || "all"}
              />
            )}

            {/* Basket Choice Step */}
            {checkout.step === "basketChoice" && (
              <BasketChoiceStep
                itemName={course.title}
                dateLabel={formattedDate}
                total={checkout.total}
                currency={currency}
                basketItemCount={basketItemCount}
                onAddToBasket={handleAddToBasket}
                onProceedToCheckout={handleProceedToBasketCheckout}
                directCheckout={directCheckout}
                basketConflict={basketConflict}
              />
            )}

          </CheckoutPanel.Main>

          <CheckoutPanel.Sidebar>
            <CheckoutSummaryCard
              title={course.title}
              thumbnail={course.images?.[0]?.url}
              description={formattedDate}
              locationLabel={summaryLocationLabel}
              isOnline={course.isOnline}
              availability={checkout.availability}
              hideDisplayAvailableTickets={course?.displaySettings?.hideDisplaysAvailableTickets ?? false}
              items={[...ticketItems, ...addOnItems]}
              total={checkout.total}
              totalRemainingBalance={checkout.totalRemainingBalance}
              currency={currency}
              discount={checkout.discountAmount}
              discountPercentage={checkout.discount?.type === "PERCENTAGE" ? checkout.discount?.value : undefined}
              cancellationPolicyUrl={cancellationPolicyUrl}
              termsSettings={termsSettings}
              customTerms={customTerms}
              timezone={timezone}
              onOpenTermsDialog={onOpenTermsDialog}
              onPolicyClick={handlePolicyClick}
              ticketPolicies={ticketPolicies.length > 0 ? ticketPolicies : undefined}
              showBundleMembershipHelp={showBundleMembershipHelp}
            />

            {/* Show bundle info if using purchased bundle */}
            {effectiveBundleID && (
              <div className="mt-4 p-4 bg-blue-100 dark:bg-blue-900 rounded-lg border border-blue-500">
                <h4 className="font-semibold text-blue-700 dark:text-blue-300 mb-1">
                  Using Bundle
                </h4>
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  Payment covered by your purchased bundle.
                </p>
              </div>
            )}
          </CheckoutPanel.Sidebar>
        </CheckoutPanel.Content>
      </CheckoutPanel>

      {/* Footer */}
      {/* Footer - hide continue button on basketChoice step (it has its own action buttons) */}
      <CheckoutFooter
        label="Date Selected"
        value={formattedDate}
        total={checkout.total}
        currency={currency}
        showBack={!checkout.isFirstStep && !(isDirectBooking && checkout.step === "tickets")}
        onBack={checkout.goToPreviousStep}
        showCancel={!!onClose}
        onCancel={onClose}
        showContinue={checkout.step !== "basketChoice"}
        onContinue={checkout.goToNextStep}
        continueText="Continue"
        continueDisabled={!checkout.canContinue()}
        isEmbedMode={isEmbedMode}
        validationMessage={
          !checkout.canContinue()
            ? checkout.step === "dates"
              ? "Please select a date to continue"
              : checkout.step === "tickets"
                ? checkout.hasMixedPolicies
                  ? "Selected tickets have different cancellation policies"
                  : "Please select at least one ticket"
                : undefined
            : undefined
        }
      />
    </ToastProvider>
  );
}
